import { inject, Injectable, LOCALE_ID } from '@angular/core';
import { SpaceFacade } from '@simOn/space';
import { map, Observable, take } from 'rxjs';
import { AgentMessageContextDto } from '../model';

/** The backend validates the tag; the Angular locale is one already, but anything odd is dropped rather than rejected. */
const BCP47_TAG = /^[a-z]{2,3}(-[a-z0-9]{2,8})*$/i;

/**
 * Builds the grounding context sent with every message. Agent endpoints ignore the apartment header,
 * so the selected space has to travel in the body; the time zone and UI language let the agent resolve
 * relative dates ("today", "next week") and phrase answers for the user.
 */
@Injectable({ providedIn: 'root' })
export class AgentContextService {
  private readonly _spaceFacade = inject(SpaceFacade);
  private readonly _locale = inject(LOCALE_ID);

  getContext$(): Observable<AgentMessageContextDto> {
    return this._spaceFacade.selectedApartmentId$.pipe(
      take(1),
      map((apartmentId) => ({
        apartmentId: apartmentId ?? null,
        workspaceId: null,
        timeZone: this._timeZone(),
        language: this._language()
      }))
    );
  }

  /** `Intl` may be unavailable or return an empty/odd id on some platforms; the backend rejects unknown ids with 400. */
  private _timeZone(): string | null {
    try {
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return timeZone && timeZone.length <= 64 ? timeZone : null;
    } catch {
      return null;
    }
  }

  private _language(): string | null {
    return BCP47_TAG.test(this._locale) ? this._locale : null;
  }
}
